<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import paymentService from '../services/payment.service'
import { type PaymentDocument } from '../services/payment-document.service'
import paymentOnlineService from '../services/payment-online.service'
import api from '../services/api'
import LanguageSelector from '../components/LanguageSelector.vue'
import { useAuthStore } from '../stores/auth'

const route = useRoute()
const { t, locale } = useI18n()
const authStore = useAuthStore()
const isLoggedIn = !!localStorage.getItem('auth_token')
const payment = ref<any>(null)
const loading = ref(true)
const error = ref(false)
const termsDocument = ref<PaymentDocument | null>(null)
const privacyDocument = ref<PaymentDocument | null>(null)
const otherDocuments = ref<PaymentDocument[]>([])
const loadingDocuments = ref(false)

// Document acceptances
const acceptTerms = ref(false)
const acceptPrivacy = ref(false)
const acceptOtherDocuments = ref<Record<string, boolean>>({})

// Document preview modal
const showDocumentModal = ref(false)
const selectedDocument = ref<PaymentDocument | null>(null)

// Payer email — pre-fill from query param, then logged-in user
const payerEmail = ref((route.query.email as string) || authStore.user?.email || '')
const processingPayment = ref(false)

const uuid = computed(() => route.params.uuid as string)
const hash = computed(() => route.params.hash as string)
const currentUrl = computed(() => window.location.href)
const returnStatus = computed(() => route.query.status as string | undefined)

// Payment processing state
const waitingForPayment = ref(false)
const pollingInterval = ref<number | null>(null)

const paymentProviderLogo = computed(() => {
  if (!payment.value?.paymentProvider) return null

  const provider = payment.value.paymentProvider.toLowerCase()
  const logoMap: Record<string, string> = {
    'payu': '/images/payment-providers/payu.png',
    'stripe': '/images/payment-providers/stripe.png',
    'paypal': '/images/payment-providers/paypal.png',
    'przelewy24': '/images/payment-providers/przelewy24.png',
    'klarna': '/images/payment-providers/klarna.png'
  }

  return logoMap[provider] || null
})

const allDocumentsAccepted = computed(() => {
  const emailValid = payerEmail.value.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerEmail.value)
  const termsAccepted = !termsDocument.value || acceptTerms.value
  const privacyAccepted = !privacyDocument.value || acceptPrivacy.value
  const othersAccepted = otherDocuments.value.every(doc => acceptOtherDocuments.value[doc.uuid])

  return emailValid && termsAccepted && privacyAccepted && othersAccepted
})

// Fetch payment data
const fetchPayment = async () => {
  loading.value = true
  error.value = false

  try {
    const response = await paymentService.getPublicPayment(uuid.value, hash.value)
    payment.value = response
  } catch (err: any) {
    console.error('Error fetching payment:', err)
    error.value = true
  } finally {
    loading.value = false
  }
}

// Show toast notification (bottom-right like other pages)
const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  const toast = document.createElement('div')
  toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white text-sm font-medium z-50 transform transition-all duration-300 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`
  toast.textContent = message
  toast.style.opacity = '0'
  toast.style.transform = 'translateY(20px)'

  document.body.appendChild(toast)

  setTimeout(() => {
    toast.style.opacity = '1'
    toast.style.transform = 'translateY(0)'
  }, 10)

  setTimeout(() => {
    toast.style.opacity = '0'
    toast.style.transform = 'translateY(20px)'
    setTimeout(() => {
      document.body.removeChild(toast)
    }, 300)
  }, 3000)
}

// Copy payment URL
const copyPaymentUrl = () => {
  const url = window.location.href
  navigator.clipboard.writeText(url)
    .then(() => {
      showToast(t('publicPayment.linkCopied'))
    })
    .catch((err) => {
      console.error('Failed to copy: ', err)
      showToast(t('publicPayment.copyLinkFailed'), 'error')
    })
}

// Copy to clipboard
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
    .then(() => {
      showToast(t('publicPayment.copiedToClipboard'))
    })
    .catch((err) => {
      console.error('Failed to copy: ', err)
      showToast(t('publicPayment.copyFailed'), 'error')
    })
}

// Auto-refresh countdown for unpaid payments
const countdown = ref(15)
let countdownInterval: any = null

const startCountdown = () => {
  if (payment.value?.status === 'unpaid') {
    countdownInterval = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) {
        clearInterval(countdownInterval)
        fetchPayment()
        countdown.value = 15
        startCountdown()
      }
    }, 1000)
  }
}

// Format amount to 2 decimal places
const formatAmount = (amount: number) => {
  return parseFloat(amount.toString()).toFixed(2)
}

// Format account number (add spaces every 4 digits)
const formatAccountNumber = (accountNumber: string) => {
  // Remove all spaces first
  const cleaned = accountNumber.replace(/\s/g, '')
  // Add space every 4 digits
  return cleaned.match(/.{1,4}/g)?.join(' ') || accountNumber
}

// Load payment documents (public endpoint - no auth required)
const loadPaymentDocuments = async () => {
  if (!payment.value?.clubUuid) return

  loadingDocuments.value = true

  try {
    const [termsResponse, privacyResponse, otherResponse] = await Promise.all([
      api.get(`v1/public/${payment.value.clubUuid}/payment-documents`, { params: { type: 'terms' } }),
      api.get(`v1/public/${payment.value.clubUuid}/payment-documents`, { params: { type: 'privacy' } }),
      api.get(`v1/public/${payment.value.clubUuid}/payment-documents`, { params: { type: 'other' } })
    ])

    termsDocument.value = termsResponse.data.documents?.[0] || null
    privacyDocument.value = privacyResponse.data.documents?.[0] || null
    otherDocuments.value = otherResponse.data.documents || []

    // Initialize acceptance state for other documents
    otherDocuments.value.forEach(doc => {
      acceptOtherDocuments.value[doc.uuid] = false
    })
  } catch (err: any) {
    console.error('Error loading payment documents:', err)
  } finally {
    loadingDocuments.value = false
  }
}

// View document
const viewDocument = (document: PaymentDocument) => {
  selectedDocument.value = document
  showDocumentModal.value = true
}

const closeDocumentModal = () => {
  showDocumentModal.value = false
  selectedDocument.value = null
}

// Online payment handlers
const handlePayOnline = async () => {
  if (!allDocumentsAccepted.value || processingPayment.value) return

  processingPayment.value = true

  try {
    const payerName = `${payment.value.playerFirstName} ${payment.value.playerLastName}`

    const result = await paymentOnlineService.initiatePayment(
      payment.value.clubUuid,
      payment.value.uuid,
      {
        payer_email: payerEmail.value.trim(),
        payer_name: payerName,
        return_url: paymentOnlineService.generateReturnUrl(payment.value.uuid, hash.value),
        cancel_url: paymentOnlineService.generateCancelUrl(payment.value.uuid, hash.value),
        other_document_uuids: otherDocuments.value.map(doc => doc.uuid),
      }
    )

    // Redirect to payment URL
    window.location.href = result.payment_url
  } catch (err: any) {
    console.error('Error initiating payment:', err)
    showToast(err.response?.data?.message || t('publicPayment.errorInitiating'), 'error')
    processingPayment.value = false
  }
}

// Start polling payment status
const startPollingPaymentStatus = () => {
  // Poll every 3 seconds
  pollingInterval.value = window.setInterval(async () => {
    try {
      const response = await paymentService.getPublicPayment(uuid.value, hash.value)
      payment.value = response

      // Stop polling if payment is paid
      if (response.status === 'paid') {
        stopPollingPaymentStatus()
        waitingForPayment.value = false
        showToast(t('publicPayment.paymentProcessed'), 'success')
      }
    } catch (err) {
      console.error('Error polling payment status:', err)
    }
  }, 3000)
}

const stopPollingPaymentStatus = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

onMounted(async () => {
  await fetchPayment()
  await loadPaymentDocuments()
  startCountdown()

  // Check if user returned from payment provider
  if (returnStatus.value === 'processing') {
    waitingForPayment.value = true
    startPollingPaymentStatus()
    showToast(t('publicPayment.waitingForConfirmation'), 'success')
  } else if (returnStatus.value === 'cancelled') {
    showToast(t('publicPayment.paymentCancelled'), 'error')
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  stopPollingPaymentStatus()
})
</script>

<template>
  <div class="bg-gray-50 min-h-screen">
    <!-- Language Selector (only for unauthenticated users) -->
    <div v-if="!isLoggedIn" class="flex justify-end px-4 pt-4">
      <LanguageSelector />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p class="mt-4 text-gray-600">{{ t('publicPayment.loading') }}</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center justify-center min-h-screen">
      <div class="text-center max-w-md mx-auto px-4">
        <div class="text-6xl mb-4">❌</div>
        <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ t('publicPayment.notFound') }}</h1>
        <p class="text-gray-600">{{ t('publicPayment.notFoundDescription') }}</p>
      </div>
    </div>

    <!-- Payment Details -->
    <div v-else-if="payment" class="max-w-2xl mx-auto px-4 py-8">
      <!-- Club Logo -->
      <div class="flex justify-center mb-6">
        <div class="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center">
          <span class="text-white text-3xl font-bold">KS</span>
        </div>
      </div>

      <!-- Waiting for Payment Confirmation Banner -->
      <div v-if="waitingForPayment && payment.status !== 'paid'" class="mb-6 bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <div class="ml-4 flex-1">
            <h3 class="text-lg font-semibold text-blue-900 mb-2">
              {{ t('publicPayment.waitingTitle') }}
            </h3>
            <p class="text-sm text-blue-700">
              {{ t('publicPayment.waitingMessage') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Payment Status -->
      <div class="mb-6 text-center">
        <div v-if="payment.status === 'paid'" class="inline-block px-8 py-4 bg-green-100 text-green-800 text-2xl font-bold rounded border-2 border-green-300">
          {{ t('publicPayment.paid') }}
        </div>
        <div v-else class="inline-block px-8 py-4 bg-red-100 text-red-800 text-2xl font-bold rounded border-2 border-red-300">
          {{ t('publicPayment.unpaid') }}
        </div>
      </div>

      <!-- Payment URL -->
      <div class="mb-6 max-w-xl mx-auto">
        <div class="flex flex-col gap-2">
          <input
            type="text"
            :value="currentUrl"
            disabled
            class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-100 text-gray-700"
          >
          <button
            @click="copyPaymentUrl"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600"
          >
            {{ t('publicPayment.copyLink') }}
          </button>
        </div>
      </div>

      <!-- Auto-refresh message for unpaid -->
      <div v-if="payment.status === 'unpaid'" class="mb-6 text-center">
        <p class="text-sm text-gray-700">
          {{ t('publicPayment.autoRefreshMessage', { seconds: countdown }) }}
        </p>
      </div>

      <!-- Payment Information -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('publicPayment.paymentInformation') }}</h2>
        <div class="space-y-3">
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.paymentNumber') }}</span>
            <span class="text-sm text-gray-900">{{ payment.number }}</span>
          </div>
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.player') }}</span>
            <span class="text-sm text-gray-900">{{ payment.playerFirstName }} {{ payment.playerLastName }}</span>
          </div>
          <div v-if="payment.playerBirthDate" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.birthDate') }}</span>
            <span class="text-sm text-gray-900">{{ payment.playerBirthDate }}</span>
          </div>
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.issueDate') }}</span>
            <span class="text-sm text-gray-900">{{ payment.createdAt }}</span>
          </div>

          <!-- Kwota -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.amountLabel') }}</span>
            <span class="text-sm text-gray-900 font-semibold">{{ formatAmount(payment.amount) }} {{ payment.currency || 'PLN' }}</span>
          </div>

          <!-- Opłata serwisowa (tylko jeśli > 0) -->
          <div v-if="payment.commission && payment.commission > 0" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.serviceFee') }}</span>
            <span class="text-sm text-gray-900">{{ formatAmount(payment.commission) }} {{ payment.currency || 'PLN' }}</span>
          </div>

          <!-- Łącznie do zapłaty (tylko jeśli commission > 0) -->
          <div v-if="payment.commission && payment.commission > 0" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.totalToPay') }}</span>
            <span class="text-sm text-gray-900 font-bold text-green-600">{{ formatAmount(payment.totalAmount || (payment.amount + payment.commission)) }} {{ payment.currency || 'PLN' }}</span>
          </div>

          <!-- Termin płatności (tylko dla niezapłaconych) -->
          <div v-if="payment.status === 'unpaid'" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.dueDate') }}</span>
            <span class="text-sm text-gray-900">{{ payment.dueDate }}</span>
          </div>

          <!-- Informacje o zapłacie (tylko dla zapłaconych) -->
          <div v-if="payment.status === 'paid'" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.dueDate') }}</span>
            <span class="text-sm text-gray-900">-</span>
          </div>
          <div v-if="payment.status === 'paid' && payment.paidAt" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.paidAt') }}</span>
            <span class="text-sm font-semibold text-green-600">{{ payment.paidAt }}</span>
          </div>
          <div v-if="payment.status === 'paid' && payment.paymentType" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.paymentMethod') }}</span>
            <span class="text-sm font-semibold text-green-600">
              <template v-if="payment.paymentType === 'online'">
                {{ t('publicPayment.paymentMethodOnline', { provider: payment.paymentProviderUsed }) }}
              </template>
              <template v-else>
                {{ t('publicPayment.paymentMethodManual') }}
              </template>
            </span>
          </div>

          <!-- Online Payment Documents (Terms & Privacy) -->
          <div v-if="payment.status === 'paid' && payment.paymentType === 'online' && payment.onlinePaymentDocuments" class="flex flex-col gap-2 py-2">
            <span class="text-sm font-medium text-gray-700 mb-1">{{ t('publicPayment.paymentDocuments') }}</span>
            <div class="flex flex-wrap gap-2">
              <button
                v-if="payment.onlinePaymentDocuments.terms"
                @click="viewDocument(payment.onlinePaymentDocuments.terms)"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition-colors"
              >
                {{ t('publicPayment.termsLink') }}
              </button>
              <button
                v-if="payment.onlinePaymentDocuments.privacy"
                @click="viewDocument(payment.onlinePaymentDocuments.privacy)"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition-colors"
              >
                {{ t('publicPayment.privacyLink') }}
              </button>
              <button
                v-for="doc in payment.onlinePaymentDocuments.other"
                :key="doc.uuid"
                @click="viewDocument(doc)"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition-colors"
              >
                {{ doc.name || doc.title }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Acceptances and Pay Online Button -->
      <div v-if="payment.status === 'unpaid' && payment.paymentProvider" class="mb-6 max-w-xl mx-auto">
        <!-- Loading documents -->
        <div v-if="loadingDocuments" class="text-center py-4">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p class="text-sm text-gray-600 mt-2">{{ t('publicPayment.loadingDocuments') }}</p>
        </div>

        <!-- Email and Document acceptances -->
        <div v-else class="bg-white border-2 border-gray-300 rounded-lg p-4 mb-4 space-y-4">
          <!-- Email field -->
          <div>
            <label for="payer-email" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('publicPayment.emailLabel') }} <span class="text-red-600">*</span>
            </label>
            <input
              id="payer-email"
              v-model="payerEmail"
              type="email"
              required
              :placeholder="t('publicPayment.emailPlaceholder')"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p class="mt-1 text-xs text-gray-500">{{ t('publicPayment.emailHint') }}</p>
          </div>

          <!-- Document acceptances -->
          <div class="space-y-3 pt-2 border-t border-gray-200">
          <!-- Terms -->
          <div v-if="termsDocument" class="flex items-start gap-3">
            <input
              id="accept-terms"
              v-model="acceptTerms"
              type="checkbox"
              class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label for="accept-terms" class="text-sm text-gray-900 cursor-pointer flex-1">
              {{ t('publicPayment.agreeWith') }}
              <button
                type="button"
                @click="viewDocument(termsDocument)"
                class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                {{ t('publicPayment.termsLinkAccept') }}
              </button>
            </label>
          </div>

          <!-- Privacy -->
          <div v-if="privacyDocument" class="flex items-start gap-3">
            <input
              id="accept-privacy"
              v-model="acceptPrivacy"
              type="checkbox"
              class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label for="accept-privacy" class="text-sm text-gray-900 cursor-pointer flex-1">
              {{ t('publicPayment.agreeWith') }}
              <button
                type="button"
                @click="viewDocument(privacyDocument)"
                class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                {{ t('publicPayment.privacyLinkAccept') }}
              </button>
            </label>
          </div>

          <!-- Other documents -->
          <div
            v-for="doc in otherDocuments"
            :key="doc.uuid"
            class="flex items-start gap-3"
          >
            <input
              :id="`accept-doc-${doc.uuid}`"
              v-model="acceptOtherDocuments[doc.uuid]"
              type="checkbox"
              class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label :for="`accept-doc-${doc.uuid}`" class="text-sm text-gray-900 cursor-pointer flex-1">
              {{ t('publicPayment.agreeWithDocument') }}
              <button
                type="button"
                @click="viewDocument(doc)"
                class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                {{ doc.name || doc.title }}
              </button>
            </label>
          </div>
          </div>
        </div>

        <!-- Pay Online Button -->
        <button
          @click="handlePayOnline"
          :disabled="loadingDocuments || !allDocumentsAccepted || processingPayment"
          class="w-full px-6 py-3 bg-green-600 text-base font-semibold text-white rounded-lg hover:bg-green-700 border-2 border-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <svg v-if="!processingPayment" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
          </svg>
          <div v-else class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          {{ processingPayment ? t('publicPayment.redirecting') : t('publicPayment.payOnline') }}
        </button>

        <!-- Payment Provider Logo -->
        <div v-if="paymentProviderLogo && !processingPayment" class="flex items-center justify-center mt-3 py-3 bg-white border border-gray-200 rounded-lg">
          <img
            :src="paymentProviderLogo"
            :alt="`${payment.paymentProvider} logo`"
            class="h-8 object-contain"
          />
        </div>

        <p v-if="!allDocumentsAccepted && !loadingDocuments && !processingPayment" class="text-xs text-red-600 text-center mt-2">
          {{ t('publicPayment.acceptAllDocuments') }}
        </p>
        <p v-else-if="!processingPayment" class="text-xs text-gray-500 text-center mt-2">
          {{ t('publicPayment.orUseTransfer') }}
        </p>
      </div>

      <!-- Transfer Details -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('publicPayment.transferDetails') }}</h2>
        <div class="space-y-3">
          <!-- Payment Name -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.paymentName') }}</span>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-900">{{ payment.paymentName }}</span>
              <button @click="copyToClipboard(payment.paymentName)" class="p-1 text-gray-600 hover:text-blue-600" :title="t('publicPayment.copy')">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Amount -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.amount') }}</span>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-900 font-semibold">{{ formatAmount(payment.amount) }} {{ payment.currency || 'PLN' }}</span>
              <button @click="copyToClipboard(formatAmount(payment.amount))" class="p-1 text-gray-600 hover:text-blue-600" :title="t('publicPayment.copy')">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Bank Name -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.bankName') }}</span>
            <span class="text-sm text-gray-900">{{ payment.bankName }}</span>
          </div>

          <!-- Recipient Name -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.recipientName') }}</span>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-900">{{ payment.recipientName }}</span>
              <button @click="copyToClipboard(payment.recipientName)" class="p-1 text-gray-600 hover:text-blue-600" :title="t('publicPayment.copy')">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Account Number -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.accountNumber') }}</span>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-900 font-mono">{{ formatAccountNumber(payment.accountNumber) }}</span>
              <button @click="copyToClipboard(payment.accountNumber.replace(/\s/g, ''))" class="p-1 text-gray-600 hover:text-blue-600" :title="t('publicPayment.copy')">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- SWIFT/BIC -->
          <div v-if="payment.swift" class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2 border-b border-gray-200">
            <span class="text-sm font-medium text-gray-700">SWIFT/BIC</span>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-900 font-mono">{{ payment.swift }}</span>
              <button @click="copyToClipboard(payment.swift)" class="p-1 text-gray-600 hover:text-blue-600" :title="t('publicPayment.copy')">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Transfer Title -->
          <div class="flex flex-col min-[500px]:flex-row min-[500px]:justify-between min-[500px]:items-center py-2">
            <span class="text-sm font-medium text-gray-700">{{ t('publicPayment.transferTitle') }}</span>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-900">{{ t('publicPayment.transferTitleValue', { number: payment.number, name: payment.paymentName }) }}</span>
              <button @click="copyToClipboard(t('publicPayment.transferTitleValue', { number: payment.number, name: payment.paymentName }))" class="p-1 text-gray-600 hover:text-blue-600" :title="t('publicPayment.copy')">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Document Preview Modal -->
    <div
      v-if="showDocumentModal && selectedDocument"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      @click.self="closeDocumentModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 class="text-xl font-semibold text-gray-900">{{ selectedDocument.title }}</h2>
            <div v-if="selectedDocument.is_active" class="flex items-center gap-4 mt-1 text-sm text-gray-600">
              <span class="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                {{ t('publicPayment.activeDocument') }}
              </span>
            </div>
          </div>
          <button
            @click="closeDocumentModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <div class="prose max-w-none" v-html="selectedDocument.content"></div>
        </div>

        <!-- Modal Footer -->
        <div class="flex items-center justify-end p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button
            @click="closeDocumentModal"
            class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('publicPayment.closeModal') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Prose styles for document content */
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  color: #111827;
  font-weight: 600;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.prose p {
  margin-top: 0.75em;
  margin-bottom: 0.75em;
}

.prose ul,
.prose ol {
  margin-top: 0.75em;
  margin-bottom: 0.75em;
  padding-left: 1.5em;
}

.prose li {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}

.prose strong {
  font-weight: 600;
  color: #111827;
}

.prose a {
  color: #2563eb;
  text-decoration: underline;
}

.prose a:hover {
  color: #1d4ed8;
}
</style>
